import React, { useCallback, useContext, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import { BookmarkBorderOutlined, BookmarkOutlined } from '@material-ui/icons';
import { useToast } from '@redshelf/redshelf-ui';

import { IconButton, IconTooltip } from 'components';
import {
  BookLocationValuesContext,
  UserAgentValuesContext,
  UserContentValuesContext,
  UserContentItemModifiersContext,
} from 'contexts';
import { useKeyboardShortcut } from 'hooks';

const useStyles = makeStyles((theme) => ({
  bookmarked: {
    fontSize: '1.5rem',
    color: theme.palette.shelfBlue[500],
  },
  bookmark: {
    fontSize: '1.5rem',
  },
}));

const BookmarkButton = ({ ...props }) => {
  const { currentBookLocation } = useContext(BookLocationValuesContext);
  const { userAgent } = useContext(UserAgentValuesContext);
  const userContent = useContext(UserContentValuesContext);
  const { toggleBookmark } = useContext(UserContentItemModifiersContext);

  const { enqueueToast } = useToast();

  const [page, setPage] = useState();

  useEffect(() => {
    if (!currentBookLocation?.page) {
      return;
    }

    setPage((prevPage) =>
      prevPage !== currentBookLocation.page
        ? currentBookLocation.page
        : prevPage,
    );
  }, [currentBookLocation?.page]);

  const classes = useStyles(props);

  // bookmark icon state
  const [pageHasBookmark, setPageHasBookmark] = useState(false);

  useEffect(() => {
    if (page) {
      const hasBookmark = Boolean(
        userContent?.bookmarksByPage?.get(page)?.length,
      );
      setPageHasBookmark(hasBookmark);
    }
  }, [page, userContent?.bookmarksByPage]);

  const toggleBookmarkAndNotify = useCallback(async () => {
    if (page) {
      const createdBookmark = await toggleBookmark(currentBookLocation);
      enqueueToast(
        createdBookmark
          ? `You've successfully created a bookmark`
          : 'Bookmark removed',
        {
          variant: 'success',
        },
      );
    }
  }, [page, currentBookLocation, toggleBookmark, enqueueToast]);

  const optAltKey = userAgent.isMac ? 'option' : 'alt';
  useKeyboardShortcut([`shift+${optAltKey}+k`], toggleBookmarkAndNotify, {
    event: 'keydown',
  });

  const bookmarkLabel = pageHasBookmark ? 'Remove Bookmark' : 'Bookmark Page';

  return (
    <IconTooltip
      disabled={!page}
      id="bookmark-label"
      title={bookmarkLabel}
      {...props}
    >
      <IconButton
        onClick={toggleBookmarkAndNotify}
        aria-label={bookmarkLabel}
        disabled={!page}
      >
        {pageHasBookmark ? (
          <BookmarkOutlined fontSize="small" className={classes.bookmarked} />
        ) : (
          <BookmarkBorderOutlined
            fontSize="small"
            className={classes.bookmark}
          />
        )}
      </IconButton>
    </IconTooltip>
  );
};

BookmarkButton.propTypes = {};

export default BookmarkButton;
